import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { initializePortalContainers } from 'config/portalContainers';

type QuerySelectorString = string;

/**
 * Селектор для поиска родительского компонента для встраивания.
 * Если указано null, будет вставляться не в контейнер, а локально
 */
export type PortalHolder = QuerySelectorString | HTMLElement | null;

const isPortalHolderQuerySelector = (
  ph: PortalHolder
): ph is QuerySelectorString => typeof ph === 'string';

export type PortalProps = {
  children?: React.ReactNode;
  portalHolder: PortalHolder;
};

const useRecomputeContainer = (
  containerRef: React.MutableRefObject<Element | null>,
  portalHolder: PortalHolder
): void => {
  const previouslySavedHolder = React.useRef<any>({});

  if (portalHolder !== previouslySavedHolder.current) {
    previouslySavedHolder.current = portalHolder;
    containerRef.current = null;
  }

  if (!containerRef.current) {
    if (portalHolder === null) {
      containerRef.current = null;
    }

    if (typeof portalHolder === 'object') {
      containerRef.current = portalHolder;
    }

    if (isPortalHolderQuerySelector(portalHolder)) {
      containerRef.current = document.querySelector(portalHolder);
    }
  }
};

type PortalType = React.ComponentType<PortalProps>;

const Portal: PortalType = ({ children, portalHolder }: PortalProps) => {
  initializePortalContainers();

  const containerRef = React.useRef<Element | null>(null);

  useRecomputeContainer(containerRef, portalHolder);

  if (portalHolder && !containerRef.current) {
    return null;
  }

  return containerRef.current ? (
    ReactDOM.createPortal(children, containerRef.current)
  ) : (
    <>{children}</>
  );
};

export default Portal;
